import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  BookOpen, 
  RefreshCw, 
  Calendar,
  AlertTriangle,
  CheckCircle,
  FileText,
  Loader2,
  ChevronDown,
  ChevronUp,
  Clock,
  Tag,
  Sparkles
} from 'lucide-react';

export default function TimelineNarrativePanel({ eventId }) {
  const [narrative, setNarrative] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    narrative: true,
    summary: true,
    keyDates: false,
    decisions: false,
    outstanding: false
  });

  useEffect(() => {
    loadNarrative();
  }, [eventId]);

  const loadNarrative = async () => {
    try {
      const response = await api.correspondence.getNarrative(eventId);
      setNarrative(response.data);
    } catch (error) {
      console.error('Failed to load narrative:', error);
      // Don't show error toast for initial load - narrative might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateNarrative = async () => {
    setGenerating(true);
    try {
      const response = await api.correspondence.generateNarrative(eventId);
      setNarrative(response.data);
      toast.success('Timeline narrative generated successfully');
    } catch (error) {
      console.error('Failed to generate narrative:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate narrative');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const response = await api.correspondence.generateSummary(eventId);
      setSummary(response.data);
      toast.success('Claim summary generated successfully');
    } catch (error) {
      console.error('Failed to generate summary:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setGeneratingSummary(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          <span className="ml-2 text-slate-500">Loading narrative...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timeline-narrative-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-slate-700" />
          <h3 className="text-lg font-barlow font-bold uppercase text-slate-900">
            Timeline Narrative & Summary
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleGenerateNarrative}
            disabled={generating}
            variant="outline"
            size="sm"
            className="border-slate-300"
            data-testid="generate-narrative-button"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <Sparkles className="h-3 w-3 mr-1 animate-pulse text-purple-500" />
                AI Processing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate Narrative
              </>
            )}
          </Button>
          <Button
            onClick={handleGenerateSummary}
            disabled={generatingSummary}
            className="bg-slate-900 text-white hover:bg-slate-800"
            size="sm"
            data-testid="generate-summary-button"
          >
            {generatingSummary ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Generate Claim Summary
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {narrative && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-blue-600 mb-1">Messages</p>
            <p className="text-2xl font-barlow font-bold text-blue-900">
              {narrative.message_count || 0}
            </p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-purple-600 mb-1">Entries</p>
            <p className="text-2xl font-barlow font-bold text-purple-900">
              {narrative.entry_count || (narrative.entries?.length || 0)}
            </p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-amber-600 mb-1">Attachments</p>
            <p className="text-2xl font-barlow font-bold text-amber-900">
              {narrative.attachment_count || 0}
            </p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-green-600 mb-1">Last Updated</p>
            <p className="text-sm font-bold text-green-900">
              {formatDate(narrative.last_generated_at)}
            </p>
          </div>
        </div>
      )}

      {/* Executive Summary Section */}
      {(narrative?.executive_summary || summary) && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-sm overflow-hidden">
          <button
            onClick={() => toggleSection('summary')}
            className="w-full flex items-center justify-between p-4 hover:bg-indigo-100/50"
          >
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-indigo-600" />
              <h4 className="text-sm font-bold uppercase tracking-wider text-indigo-900">
                Executive Summary
              </h4>
              <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                AI Generated
              </Badge>
            </div>
            {expandedSections.summary ? (
              <ChevronUp className="h-4 w-4 text-indigo-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-indigo-600" />
            )}
          </button>
          {expandedSections.summary && (
            <div className="px-4 pb-4">
              <div className="bg-white border border-indigo-100 rounded-sm p-4">
                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {summary?.summary || narrative?.executive_summary || 'No summary generated yet. Click "Generate Claim Summary" to create one.'}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Timeline Narrative Section */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <button
          onClick={() => toggleSection('narrative')}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
        >
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-slate-600" />
            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900">
              Full Timeline Narrative
            </h4>
          </div>
          {expandedSections.narrative ? (
            <ChevronUp className="h-4 w-4 text-slate-600" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-600" />
          )}
        </button>
        {expandedSections.narrative && (
          <div className="px-4 pb-4">
            {narrative?.full_narrative_text ? (
              <div className="bg-slate-50 border border-slate-100 rounded-sm p-4 max-h-[400px] overflow-y-auto">
                <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                  {narrative.full_narrative_text}
                </pre>
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p>No narrative generated yet.</p>
                <Button
                  onClick={handleGenerateNarrative}
                  variant="outline"
                  className="mt-4"
                >
                  Generate Narrative
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Key Dates Section */}
      {narrative?.key_dates && narrative.key_dates.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <button
            onClick={() => toggleSection('keyDates')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
          >
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900">
                Key Dates & Milestones
              </h4>
              <Badge variant="secondary">{narrative.key_dates.length}</Badge>
            </div>
            {expandedSections.keyDates ? (
              <ChevronUp className="h-4 w-4 text-slate-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-600" />
            )}
          </button>
          {expandedSections.keyDates && (
            <div className="px-4 pb-4">
              <div className="space-y-2">
                {narrative.key_dates.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50/50 rounded-sm">
                    <Calendar className="h-4 w-4 text-blue-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-slate-800">{item.date || item}</p>
                      {item.description && (
                        <p className="text-sm text-slate-600">{item.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Key Decisions Section */}
      {narrative?.key_decisions && narrative.key_decisions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <button
            onClick={() => toggleSection('decisions')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900">
                Key Decisions
              </h4>
              <Badge variant="secondary">{narrative.key_decisions.length}</Badge>
            </div>
            {expandedSections.decisions ? (
              <ChevronUp className="h-4 w-4 text-slate-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-600" />
            )}
          </button>
          {expandedSections.decisions && (
            <div className="px-4 pb-4">
              <ul className="space-y-2">
                {narrative.key_decisions.map((decision, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-2 bg-green-50/50 rounded-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{decision}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Outstanding Items Section */}
      {narrative?.outstanding_items && narrative.outstanding_items.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <button
            onClick={() => toggleSection('outstanding')}
            className="w-full flex items-center justify-between p-4 hover:bg-slate-50"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <h4 className="text-sm font-bold uppercase tracking-wider text-slate-900">
                Outstanding Items
              </h4>
              <Badge className="bg-amber-100 text-amber-700">{narrative.outstanding_items.length}</Badge>
            </div>
            {expandedSections.outstanding ? (
              <ChevronUp className="h-4 w-4 text-slate-600" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-600" />
            )}
          </button>
          {expandedSections.outstanding && (
            <div className="px-4 pb-4">
              <ul className="space-y-2">
                {narrative.outstanding_items.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2 p-2 bg-amber-50/50 rounded-sm">
                    <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Contractual Tags */}
      {narrative?.contractual_tags && narrative.contractual_tags.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <Tag className="h-4 w-4 text-slate-500" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Tags:</span>
          {narrative.contractual_tags.map((tag, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {tag}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
